import { ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment-timezone';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DELIVERY_DATE_TYPE_OPTIONS, DELIVERY_WINDOW_TYPE_OPTIONS } from 'src/app/MyQuote/AddQuote/Steps/addQuoteSteps.const';
import { getSpecialInstructionValues, resolveSpecialInstructionText } from 'src/app/Orders/order.helpers';
import { getTimeUntilStart } from 'src/app/Orders/schedule.helpers';
import HeaderCmp from 'src/components/HeaderCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { ORDER_STATUS } from 'src/constants/common';
import { appearance } from 'src/redux/basic.slice';
import { useAppSelector } from 'src/redux/hooks';
import { getDateWithSuffixFormat } from 'src/utils/CommonFunctions';

import { useRadarData } from '../../hooks/useRadarData';
import { formatTimeRangeDisplay, formatTimeRangeDisplayNew, getTimeZoneOption, makeFormatScheduleDisplay } from '../track.helpers';

import CardInfoRow from './common/CardInfoRow';
import ContactCopyButton from './common/ContactCopyButton';
import InfoRow from './common/InfoRow';
import { SHARE_MOBILE_CARD, SHARE_MOBILE_CARD_TITLE } from './common/shareMobile.styles';

// isShareMobile: share-link mobile sheet (Schedule tab) — Figma card layout, same data + interactions.
const DeliveredStateSection: React.FC<{ isShareLink?: boolean; titleContent?: React.ReactNode; isShareMobile?: boolean }> = ({ isShareLink = false, titleContent, isShareMobile = false }) => {
    const { t } = useTranslation();
    const { trackOrderDetail: orderDetail, isDetailLoading: isLoading, selectedLeg } = useRadarData();
    const timeRef = useRef<HTMLParagraphElement>(null);
    const appearanceData = useAppSelector(appearance);
    const userTimezone = appearanceData?.timezone || 'UTC';

    const [isDetailOpen, setIsDetailOpen] = useState(true);

    // Follow the leg pager (same as PickupStateSection) — this card must show the VIEWED
    // leg's delivery schedule/consignee/instructions, not always leg 1's.
    const activeLeg = useMemo(() => selectedLeg ?? orderDetail?.legs?.[0] ?? null, [selectedLeg, orderDetail]);

    // Special Instructions come from the LEG (order_legs.specialInstruction) — the same
    // field WAL's radar reads. orders.specialNotes is the legacy quote-era column and is
    // empty on orders created through the current flow, which is why this row rendered blank.
    const resolvedSpecialInstruction = useMemo(
        () =>
            resolveSpecialInstructionText(
                activeLeg?.specialInstruction,
                getSpecialInstructionValues({
                    // Quote ID is an internal reference the public share page hides (see
                    // hideInternalReferenceRows in OrderDetailsSection), so @[quoteNumber]
                    // must resolve to blank there instead of leaking it into the text.
                    quoteNumber: isShareLink ? null : activeLeg?.quoteCode,
                    additionalServices: orderDetail?.additionalServices,
                })
            ),
        [activeLeg?.specialInstruction, activeLeg?.quoteCode, orderDetail?.additionalServices, isShareLink]
    );

    const schedule = activeLeg?.schedule ?? null;
    const consignee = activeLeg?.consignee ?? null;
    // No consignee zone → no source to convert FROM. Falling back to the viewer's zone
    // would make source and target equal, silently leaving Local Time unconverted.
    const deliveryTimezone = consignee?.timezone || null;

    const deliverySchedule = useMemo(() => makeFormatScheduleDisplay('delivery', schedule, deliveryTimezone), [schedule, deliveryTimezone]);
    const deliveryDisplay = deliverySchedule.display;

    const actualDeliveryDatetime = schedule?.actualDeliveryDatetime || null;
    const isDeliveredOrder = Boolean(orderDetail?.orderStatus && orderDetail.orderStatus !== ORDER_STATUS.DISPATCHED && orderDetail.orderStatus !== ORDER_STATUS.IN_TRANSIT && actualDeliveryDatetime);
    const actualDeliveryMoment = actualDeliveryDatetime ? moment.utc(actualDeliveryDatetime).tz(deliveryTimezone || userTimezone) : null;

    // Moment escapes literal text wrapped in [..]; injecting the translated word into the
    // brackets keeps it a single format string while letting i18n control the separator.
    const atToken = `[${t('Common.at')}]`;
    const deliveredLabel = actualDeliveryMoment
        ? `${t('Radar.Delivered')} ${getDateWithSuffixFormat(
              actualDeliveryMoment.calendar(null, {
                  sameDay: `[Today], ${atToken} HH:mm`,
                  lastDay: `[Yesterday], ${atToken} HH:mm`,
                  lastWeek: `MMM Do, YYYY ${atToken} HH:mm`,
                  nextDay: `MMM Do, YYYY ${atToken} HH:mm`,
                  nextWeek: `MMM Do, YYYY ${atToken} HH:mm`,
                  sameElse: `MMM Do, YYYY ${atToken} HH:mm`,
              })
          )}`
        : '';

    const sensitivityLabel = DELIVERY_DATE_TYPE_OPTIONS.find((opt) => opt.value === schedule?.currentDeliverySensitivity)?.label;
    const windowTypeLabel = DELIVERY_WINDOW_TYPE_OPTIONS.find((opt) => opt.value === schedule?.currentDeliveryWindowType)?.label;
    // Local Time is converted into the VIEWER's timezone, so the abbreviation beside it must
    // be the viewer's too — not the consignee's.
    const timezoneAbbr = getTimeZoneOption(userTimezone)?.abbr ?? '';

    const displayMonth = isDeliveredOrder ? actualDeliveryMoment?.format('MMM').toUpperCase() ?? '-' : deliveryDisplay?.month ?? '-';
    const displayDay = isDeliveredOrder ? actualDeliveryMoment?.format('DD') ?? '-' : deliveryDisplay?.day ?? '-';

    const hasTimeRange = deliveryDisplay?.timeRange && deliveryDisplay.timeRange !== '-';
    const windowValue = windowTypeLabel || hasTimeRange ? `${windowTypeLabel ? `${windowTypeLabel} ` : ''}${hasTimeRange ? deliveryDisplay?.timeRange : ''}`.trim() : '-';
    // WAL renders this row as soon as a schedule exists: the converted range when there IS one,
    // otherwise the viewer's zone abbreviation on its own ("EDT"). Requiring a real range meant
    // every Business-Hours schedule (no start/end time, so timeRangeLocal falls back to "-")
    // showed a bare dash here while WAL showed the zone.
    const localTimeRange = deliveryDisplay?.timeRangeLocal && deliveryDisplay.timeRangeLocal !== '-' ? deliveryDisplay.timeRangeLocal : '';
    const localTimeLabel = timezoneAbbr || userTimezone || '';
    const localTimeValue = deliveryDisplay?.timeRangeLocal ? (
        <span className="text-xs leading-[18px] font-medium text-textSecondary inline-flex items-center gap-x-1">
            {localTimeRange && <span>{localTimeRange}</span>}
            {/* Abbreviations are ambiguous across zones (several read "EDT"), so the full IANA
                name sits behind a tooltip — same affordance as WAL. */}
            <TooltipCmp message={userTimezone || ''}>
                <span className="cursor-default">{localTimeLabel}</span>
            </TooltipCmp>
        </span>
    ) : undefined;
    // formattedDateRange already carries <sup> ordinals from the shared helper — appending the
    // time phrase mirrors wal-frontend-v2's "Expected Delivery" row.
    const expectedDeliveryHtml =
        deliveryDisplay?.formattedDateRange && deliveryDisplay.formattedDateRange !== '-'
            ? `${deliveryDisplay.formattedDateRange}${formatTimeRangeDisplay(deliveryDisplay, t) ? ` ${formatTimeRangeDisplay(deliveryDisplay, t)}` : ''}`
            : undefined;
    // "Delivery Window (09:00 to 16:00 EDT)" + a countdown when the window opens within 24h.
    const windowPhrase = formatTimeRangeDisplayNew(deliveryDisplay, t, 'Delivery');
    const timeUntilStart = deliverySchedule.startDate ? getTimeUntilStart(deliverySchedule.startDate, t) : '';

    if (isShareMobile) {
        return (
            <>
                {/* Delivery Schedule — switcher + collapse toggle, date row, detail rows */}
                <div className={`${SHARE_MOBILE_CARD} p-2 flex flex-col gap-2`}>
                    <div className="flex items-center gap-2 pb-1">
                        <div className="flex-1 min-w-0 flex items-center">{titleContent ?? <p className={SHARE_MOBILE_CARD_TITLE}>{t('Radar.Delivery_Schedule')}</p>}</div>
                    </div>

                    <div className="flex items-start gap-1.5">
                        <div
                            className={`h-[38px] min-w-[38px] px-[7px] py-1.5 rounded border border-utilityGray200 bg-white flex flex-col items-center justify-center text-center flex-none ${
                                isLoading ? 'custom-loading' : ''
                            }`}
                        >
                            {displayMonth && displayMonth !== '-' ? (
                                <>
                                    <span className="text-[10px] font-semibold text-primary700 leading-normal uppercase">{isLoading ? 'lorem' : displayMonth}</span>
                                    <span className="text-[10px] font-semibold text-primary700 leading-[14px]">{isLoading ? 'ip' : displayDay}</span>
                                </>
                            ) : (
                                <p className="text-[10px] font-semibold text-primary700">{t('Common.N/A')}</p>
                            )}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <p
                                className={`text-xs font-medium leading-[18px] text-grayLight900 ${isLoading ? 'custom-loading' : ''}`}
                                // Generated by internal utilities (getDateWithSuffixFormat) — safe by construction.
                                dangerouslySetInnerHTML={{
                                    __html: isLoading ? 'lorem ipsum dolor' : isDeliveredOrder ? deliveredLabel : deliveryDisplay?.formattedDateRange ?? '-',
                                }}
                            />
                            <div className={`text-xs leading-[18px] font-medium text-grayLight600 flex items-center gap-x-1.5 min-w-0 ${isLoading ? 'custom-loading' : ''}`}>
                                {isLoading || !isDeliveredOrder ? (
                                    <>
                                        <TruncateTextCmp message={isLoading ? '' : windowPhrase} refName={timeRef}>
                                            <p className="truncate" ref={timeRef}>
                                                {isLoading ? 'lorem ipsum dolor sit amet' : windowPhrase || windowTypeLabel || '-'}
                                            </p>
                                        </TruncateTextCmp>
                                        {windowPhrase && timeUntilStart ? (
                                            <p className="flex-none flex items-center gap-x-1">
                                                <span className="bg-successSecondary w-1.5 h-1.5 rounded-full"></span>
                                                {`${t('Common.in')} ${timeUntilStart}`}
                                            </p>
                                        ) : (
                                            ''
                                        )}
                                    </>
                                ) : (
                                    t('Radar.Delivery_Completed')
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Detail rows — always shown in full on the share sheet */}
                    <div className="border-t border-utilityGray200 pt-2 flex flex-col gap-2">
                        <CardInfoRow isShareMobile label={t('Radar.Delivery_Sensitivity')} value={sensitivityLabel} isLoading={isLoading} isLabelLoading={isLoading} />
                        <CardInfoRow isShareMobile label={t('Radar.Delivery_Window')} value={windowValue} isLoading={isLoading} isLabelLoading={isLoading} />
                        <CardInfoRow isShareMobile label={t('Radar.Expected_Delivery')} value={expectedDeliveryHtml} isLoading={isLoading} isLabelLoading={isLoading} align="start" />
                        <CardInfoRow isShareMobile label={t('Radar.Local_Time')} value={localTimeValue} isLoading={isLoading} isLabelLoading={isLoading} />
                    </div>
                </div>

                {/* Scheduling Details */}
                <div className={`${SHARE_MOBILE_CARD} p-2 flex flex-col gap-2`}>
                    <p className={`${SHARE_MOBILE_CARD_TITLE} pb-1`}>{t('ShareRadarOrder.Scheduling_Details')}</p>
                    <InfoRow isShareMobile label={t('Orders.Consignee_Name')} value={consignee?.companyName} isLoading={isLoading} isLabelLoading={isLoading} />
                    <InfoRow
                        isShareMobile
                        label={t('Orders.Consignee_Contact')}
                        value={consignee?.contactName}
                        icon={consignee?.contactName ? <ContactCopyButton phoneNumber={consignee?.phoneNumber} /> : undefined}
                        isLoading={isLoading}
                        isLabelLoading={isLoading}
                    />
                    <InfoRow isShareMobile label={t('Radar.Consignee_Address')} value={consignee?.fullAddress} isLoading={isLoading} isLabelLoading={isLoading} />
                    <InfoRow
                        isShareMobile
                        label={t('Radar.Delivery_Instructions')}
                        value={activeLeg?.deliveryInstruction ?? undefined}
                        isLoading={isLoading}
                        isLabelLoading={isLoading}
                        align="start"
                    />
                    <InfoRow isShareMobile label={t('Radar.Special_Instructions')} value={resolvedSpecialInstruction || undefined} isLoading={isLoading} isLabelLoading={isLoading} align="start" />
                </div>
            </>
        );
    }

    return (
        <div className="w-full rounded-lg shadow-md bg-white overflow-hidden">
            {/* Header */}
            <HeaderCmp title={titleContent ? '' : t('Radar.Delivery_Schedule')} leftSideContent={titleContent} isHeaderWithBg={false} parentClassName="!border-b-0 px-4" />

            {/* Body */}
            <div className="px-4 pb-4 flex flex-col gap-y-3">
                {/* Date card */}
                <div className="p-3 rounded-lg border flex flex-col border-utilityGray200 bg-gray50 overflow-hidden">
                    <div className="flex items-center gap-x-1 cursor-pointer" onClick={() => setIsDetailOpen((prev) => !prev)}>
                        <div
                            className={`table-profile-customer-circle-xl rounded-md bg-white flex flex-col items-center justify-center text-center flex-none gap-[1px] w-9 h-9 ${
                                isLoading ? 'custom-loading' : ''
                            }`}
                        >
                            {displayMonth && displayMonth !== '-' ? (
                                <>
                                    <span className="text-[10px] font-semibold text-primary leading-[12px] uppercase tracking-wide">{isLoading ? 'lorem' : displayMonth}</span>
                                    <span className="text-[10px] font-semibold text-primary leading-[14px]">{isLoading ? 'ip' : displayDay}</span>
                                </>
                            ) : (
                                <p>{t('Common.N/A')}</p>
                            )}
                        </div>

                        <div className="flex-1 min-w-0">
                            <p
                                className={`text-[12px] font-semibold leading-[18px] text-grayLight900 ${isLoading ? 'custom-loading' : ''}`}
                                // Content is generated by internal utilities (getDateWithSuffixFormat) — not user
                                // input. DOMPurify is not a project dependency; this is safe by construction.
                                dangerouslySetInnerHTML={{
                                    __html: isLoading ? 'lorem ipsum dolor' : isDeliveredOrder ? deliveredLabel : deliveryDisplay?.formattedDateRange ?? '-',
                                }}
                            />
                            <div className={`text-xs leading-[18px] font-medium text-grayLight600 flex items-center gap-x-1.5 ${isLoading ? 'custom-loading' : ''}`}>
                                {isLoading || !isDeliveredOrder ? (
                                    <>
                                        <TruncateTextCmp message={isLoading ? '' : windowPhrase} refName={timeRef}>
                                            <p className="truncate" ref={timeRef}>
                                                {isLoading ? 'lorem ipsum dolor sit amet' : windowPhrase || windowTypeLabel || '-'}
                                            </p>
                                        </TruncateTextCmp>
                                        {windowPhrase && timeUntilStart ? (
                                            <p className="flex-none flex items-center gap-x-1">
                                                <span className="bg-successSecondary w-1.5 h-1.5 rounded-full"></span>
                                                {`${t('Common.in')} ${timeUntilStart}`}
                                            </p>
                                        ) : (
                                            ''
                                        )}
                                    </>
                                ) : (
                                    t('Radar.Delivery_Completed')
                                )}
                            </div>
                        </div>

                        <TooltipCmp message={isDetailOpen ? t('Common.Collapse') : t('Common.Expand')}>
                            <ChevronDown className={`w-4 h-4 text-primary flex-none transition-transform duration-200 ${isDetailOpen ? 'rotate-180' : ''}`} />
                        </TooltipCmp>
                    </div>

                    {/* Collapsible detail rows */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateRows: isDetailOpen ? '1fr' : '0fr',
                            transition: 'grid-template-rows 300ms ease',
                        }}
                    >
                        <div className="overflow-hidden">
                            <div className="border mt-3 bg-white border-utilityGray200 px-3 py-3 rounded-md flex flex-col gap-y-2">
                                <CardInfoRow label={t('Radar.Delivery_Sensitivity')} value={sensitivityLabel} isLoading={isLoading} isLabelLoading={isLoading} />
                                <CardInfoRow label={t('Radar.Delivery_Window')} value={windowValue} isLoading={isLoading} isLabelLoading={isLoading} />
                                {/* expectedDeliveryHtml carries <sup> ordinals — CardInfoRow's string path renders
                                    it as HTML, so the tags don't show up as literal text. */}
                                <CardInfoRow label={t('Radar.Expected_Delivery')} value={expectedDeliveryHtml} isLoading={isLoading} isLabelLoading={isLoading} align="start" />
                                <CardInfoRow label={t('Radar.Local_Time')} value={localTimeValue} isLoading={isLoading} isLabelLoading={isLoading} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Info rows */}
                <div className="flex flex-col gap-y-1.5">
                    <InfoRow label={t('Orders.Consignee_Name')} value={consignee?.companyName} isLoading={isLoading} isLabelLoading={isLoading} />
                    <InfoRow
                        label={t('Orders.Consignee_Contact')}
                        value={consignee?.contactName}
                        icon={consignee?.contactName ? <ContactCopyButton phoneNumber={consignee?.phoneNumber} /> : undefined}
                        isLoading={isLoading}
                        isLabelLoading={isLoading}
                    />
                    <InfoRow label={t('Radar.Consignee_Address')} value={consignee?.fullAddress} isLoading={isLoading} isLabelLoading={isLoading} align="start" />
                    <InfoRow label={t('Radar.Delivery_Instructions')} value={activeLeg?.deliveryInstruction ?? undefined} isLoading={isLoading} isLabelLoading={isLoading} align="start" />
                    <InfoRow label={t('Radar.Special_Instructions')} value={resolvedSpecialInstruction || undefined} isLoading={isLoading} isLabelLoading={isLoading} align="start" />
                </div>
            </div>
        </div>
    );
};

export default DeliveredStateSection;
